<template>
	<section class="pc-shorts">
		<div class="pc-shorts__head">
			<h2 class="pc-shorts__title">{{ t("square.shorts.title") }}</h2>
			<button class="pc-shorts__more" type="button" @click="handleMore">
				{{ t("square.shorts.more") }}
			</button>
		</div>
		<div class="pc-shorts__grid">
			<article
				v-for="(item, index) in shortList"
				:key="`${item.title}-${index}`"
				class="short-card"
				:ref="el => bindShortCardRef(el, item, index)"
				@click="handleCardClick(item, index)"
			>
				<div class="short-card__cover-wrap">
					<img class="short-card__cover" :src="item.cover" :alt="item.title" />
					<span
						class="short-card__play"
						aria-hidden="true"
						@click.stop="handlePlayClick(item, index)"
					></span>
				</div>
				<div class="short-card__body">
					<p class="short-card__title">{{ item.title }}</p>
					<div class="short-card__meta">
						<div class="short-card__author">
							<img class="short-card__avatar" :src="item.avatar" :alt="item.author" />
							<span>{{ item.author }}</span>
						</div>
						<div class="short-card__views">
							<img :src="item.viewIcon" alt="" aria-hidden="true" />
							<span>{{ item.views }}</span>
						</div>
					</div>
				</div>
			</article>
		</div>
	</section>
</template>

<script setup>
import { computed, inject } from "vue";
import { useSquarePcPageData } from "@variants/seer/square/composables/useSquarePageData";
import { useI18n } from "~/composables/useI18n";
import {
	buildContentParams,
	MEDIA_TYPES,
	useMediaCardImpression,
} from "../../../composables/useMediaSquareTracking";
import viewIcon from "../../../images/eye.png";
import channelAvatar from "../../../images/video_avatar.png";
import coverImage from "../../../images/video_view.png";

const { t } = useI18n();
const pageData = useSquarePcPageData();
const mediaTracking = inject("mediaSquareTracking", null);

const { bindCardRef: bindShortCardRef } = useMediaCardImpression({
	track: (eventName, params) => mediaTracking?.track(eventName, params),
	impressionEvent: "short_card_impression",
	getItemParams: (item, index) =>
		buildContentParams(item, {
			position: index,
			authorFallback: t("square.article.defaultAuthor"),
		}),
});

const shortList = computed(() =>
	(pageData.value?.shorts || []).slice(0, 8).map(item => ({
		...item,
		author: item?.nickname || "",
		views: item?.view_count ?? 0,
		cover: item?.cover || coverImage,
		avatar: item?.avatar || channelAvatar,
		videoUrl: Array.isArray(item?.media_url) ? item.media_url[0] || "" : "",
		viewIcon,
	}))
);

const handleMore = () => {
	mediaTracking?.trackMediaMoreClick({
		sectionId: "shorts",
		mediaType: MEDIA_TYPES.SHORT,
		currentCount: shortList.value.length,
	});
	navigateTo("/seer/square/shorts");
};

const handleCardClick = (item, index) => {
	if (!item?.id) {
		navigateTo("/seer/square");
		return;
	}
	mediaTracking?.trackShortCardClick(item, index, t("square.article.defaultAuthor"));
	navigateTo(`/seer/square-detail/${item.id}`);
};

const handlePlayClick = (item, index) => {
	if (!item?.id) return;
	mediaTracking?.trackVideoPlayClick(item, {
		mediaType: MEDIA_TYPES.SHORT,
		position: index,
		authorFallback: t("square.article.defaultAuthor"),
	});
	navigateTo(`/seer/square-detail/${item.id}`);
};
</script>

<style scoped lang="scss">
.pc-shorts {
	width: 100%;
	margin: 40px 0 100px;
	box-sizing: border-box;
	border-radius: 20px;
	box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);
	padding: 30px;

	&__head {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 30px;
	}

	&__title {
		font-size: 28px;
		color: #333333;
		font-weight: 500;
	}

	&__more {
		font-size: 18px;
		color: #999999;
		padding: 3px 30px;
		font-weight: normal;
		border-radius: 8px;
		border: 1px solid #dfdfdf;
		background-color: transparent;
	}

	&__grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 16px;
		width: 100%;
	}
}

.short-card {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: #ffffff;
	border-radius: 10px;
	overflow: hidden;
	cursor: pointer;
	border: 1px solid #efefef;
	transition: box-shadow 0.2s ease;

	&:hover {
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
	}

	&__cover-wrap {
		position: relative;
		border-radius: 10px 10px 0 0;
		overflow: hidden;
	}

	&__cover {
		display: block;
		width: 100%;
		aspect-ratio: 47 / 65;
		object-fit: cover;
	}

	&__play {
		position: absolute;
		top: 10px;
		right: 10px;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: rgba(74, 74, 74, 0.6);

		&::before {
			content: "";
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-35%, -50%);
			border-top: 6px solid transparent;
			border-bottom: 6px solid transparent;
			border-left: 9px solid #fff;
		}
	}

	&__body {
		display: flex;
		flex-direction: column;
		flex: 1;
		padding: 10px 10px 20px;
	}

	&__title {
		margin: 0 0 10px;
		min-height: calc(1.4em * 2);
		color: #222222;
		font-size: 16px;
		line-height: 1.4;
		font-weight: 500;
		display: -webkit-box;
		line-clamp: 2;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__meta {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: auto;
		color: #a0a0a0;
		font-size: 14px;
	}

	&__author,
	&__views {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
	}

	&__author span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__avatar {
		width: 20px;
		height: 20px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	&__views {
		flex-shrink: 0;
	}

	&__views img {
		width: 16px;
		height: 16px;
	}
}
</style>
